import { useMemo, type ReactNode } from "react";
import type { Section } from "../types";

interface SectionsTableProps {
  sections: Section[];
  /** IANA timezone name used to render the Time column (e.g. `America/New_York`). */
  timeZone: string;
  /** Optional empty-state override; defaults to the Phase 4 hint copy. */
  emptyState?: ReactNode;
  /**
   * Phase 5 affordance — when supplied, rows become keyboard- and
   * mouse-activatable and clicking opens the `SectionRosterPanel`.
   */
  onSectionClick?: (section: Section) => void;
}

/**
 * Stable sort: by `normalizedCourseId` ASC then `sectionLabel` ASC, with
 * nulls treated as "" so they sort first. Pure helper kept module-level so
 * the unit test can assert ordering without remounting the component.
 */
function sortSections(sections: Section[]): Section[] {
  return [...sections].sort((a, b) => {
    const courseCmp = a.normalizedCourseId.localeCompare(b.normalizedCourseId);
    if (courseCmp !== 0) return courseCmp;
    const aLabel = a.sectionLabel ?? "";
    const bLabel = b.sectionLabel ?? "";
    return aLabel.localeCompare(bLabel);
  });
}

/**
 * Formats a UTC ISO timestamp in the schedule's local timezone as `HH:mm`.
 * Returns `null` when the input is null OR when the timezone is invalid (in
 * which case the caller falls back to an em-dash).
 */
function formatHourMinute(iso: string | null, timeZone: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone,
    }).format(date);
  } catch {
    return null;
  }
}

/** Renders the dash placeholder used wherever a value is absent. */
function Dash() {
  return <span className="text-text-faint">—</span>;
}

/**
 * Pure presentation of the Sections table. Columns: Course ID, Section,
 * Teacher, Room, Time, Capacity, Assigned. Sorting and formatting live here;
 * the parent tab handles loading / error / empty states above the table.
 *
 * Slice 3 made the whole row clickable (when `onSectionClick` is supplied),
 * so the row IS the "view roster" affordance. The legacy disabled button
 * column was misleading after that change and has been dropped.
 */
export function SectionsTable({
  sections,
  timeZone,
  emptyState,
  onSectionClick,
}: SectionsTableProps) {
  const sorted = useMemo(() => sortSections(sections), [sections]);

  if (sorted.length === 0) {
    return (
      <div
        data-testid="sections-empty"
        className="bg-surface border border-border-subtle rounded-lg p-6 text-sm text-text-muted"
      >
        {emptyState ??
          "No sections — upload a calendar with course IDs in event descriptions."}
      </div>
    );
  }

  return (
    <div
      data-testid="sections-table"
      className="bg-surface border border-border-subtle rounded-lg overflow-hidden"
    >
      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr className="border-b border-border-subtle">
            <th className="text-left px-4 py-3 font-semibold text-text-muted text-[12px] uppercase tracking-wider">
              Course ID
            </th>
            <th className="text-left px-4 py-3 font-semibold text-text-muted text-[12px] uppercase tracking-wider">
              Section
            </th>
            <th className="text-left px-4 py-3 font-semibold text-text-muted text-[12px] uppercase tracking-wider">
              Teacher
            </th>
            <th className="text-left px-4 py-3 font-semibold text-text-muted text-[12px] uppercase tracking-wider">
              Room
            </th>
            <th className="text-left px-4 py-3 font-semibold text-text-muted text-[12px] uppercase tracking-wider">
              Time
            </th>
            <th className="text-right px-4 py-3 font-semibold text-text-muted text-[12px] uppercase tracking-wider">
              Capacity
            </th>
            <th className="text-right px-4 py-3 font-semibold text-text-muted text-[12px] uppercase tracking-wider">
              Assigned
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((s) => {
            const key = `${s.normalizedCourseId}::${s.sectionLabel ?? ""}`;
            const start = formatHourMinute(s.earliestStartUtc, timeZone);
            const end = formatHourMinute(s.latestEndUtc, timeZone);
            const timeText =
              start && end ? `${start} – ${end}` : (start ?? end ?? null);

            const interactive = !!onSectionClick;
            return (
              <tr
                key={key}
                data-testid={`section-row-${key}`}
                className={`border-b border-border-subtle${interactive ? " cursor-pointer hover:bg-surface-2/30" : ""}`}
                onClick={interactive ? () => onSectionClick!(s) : undefined}
                onKeyDown={
                  interactive
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onSectionClick!(s);
                        }
                      }
                    : undefined
                }
                tabIndex={interactive ? 0 : undefined}
                role={interactive ? "button" : undefined}
              >
                <td className="px-4 py-3">
                  <div className="font-mono text-blue-300 font-semibold">
                    {(s.acceptedCourseIds.length > 0
                      ? s.acceptedCourseIds
                      : [s.courseId]
                    ).join(" / ")}
                  </div>
                  {s.courseName && (
                    <div className="text-[12px] text-text-muted">
                      {s.courseName}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-text-soft">
                  {s.sectionLabel ?? <Dash />}
                </td>
                <td className="px-4 py-3 text-text-soft">
                  {s.teacherEmail ?? <Dash />}
                </td>
                <td className="px-4 py-3 text-text-soft">
                  {s.roomName ?? <Dash />}
                </td>
                <td className="px-4 py-3 text-text-muted">
                  {timeText ?? <Dash />}
                </td>
                <td className="px-4 py-3 text-right text-text-soft">
                  {s.capacity ?? <Dash />}
                </td>
                <td
                  className="px-4 py-3 text-right text-text-soft"
                  data-testid={`section-assigned-${key}`}
                >
                  {s.assignedCount ?? <Dash />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
